import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { setAnalyticsUserId } from '../utils/analytics';

export interface AuthUser {
    _id: string;
    email: string;
    fullName: string;
    role: 'user' | 'admin';
    freeDownloadsRemaining?: number;
    isBlocked?: boolean;
}

const USER_TOKEN_KEY = 'fcv_user_token';
const USER_KEY = 'fcv_user';
const ADMIN_TOKEN_KEY = 'fcv_admin_token';
const ADMIN_KEY = 'fcv_admin';
/** Anciennes clés partagées (migration) */
const LEGACY_TOKEN = 'token';
const LEGACY_USER = 'user';

interface UserAuthContextType {
    user: AuthUser | null;
    token: string | null;
    isAuthenticated: boolean;
    /** Toujours false côté portail utilisateur (l’admin a sa propre session) */
    isAdmin: boolean;
    login: (user: AuthUser, token: string) => void;
    logout: () => void;
    loading: boolean;
}

interface AdminAuthContextType {
    user: AuthUser | null;
    token: string | null;
    isAuthenticated: boolean;
    login: (user: AuthUser, token: string) => void;
    logout: () => void;
    loading: boolean;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);
const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

function readJson<T>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
}

function migrateLegacySessions(): void {
    const legacyToken = localStorage.getItem(LEGACY_TOKEN);
    const legacyUser = readJson<AuthUser>(LEGACY_USER);
    if (!legacyToken || !legacyUser) {
        localStorage.removeItem(LEGACY_TOKEN);
        localStorage.removeItem(LEGACY_USER);
        return;
    }

    if (legacyUser.role === 'admin') {
        if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
            localStorage.setItem(ADMIN_TOKEN_KEY, legacyToken);
            localStorage.setItem(ADMIN_KEY, JSON.stringify(legacyUser));
        }
    } else if (!localStorage.getItem(USER_TOKEN_KEY)) {
        localStorage.setItem(USER_TOKEN_KEY, legacyToken);
        localStorage.setItem(USER_KEY, JSON.stringify(legacyUser));
    }

    localStorage.removeItem(LEGACY_TOKEN);
    localStorage.removeItem(LEGACY_USER);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [userToken, setUserToken] = useState<string | null>(null);
    const [admin, setAdmin] = useState<AuthUser | null>(null);
    const [adminToken, setAdminToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        migrateLegacySessions();
        const ut = localStorage.getItem(USER_TOKEN_KEY);
        const uu = readJson<AuthUser>(USER_KEY);
        if (ut && uu && uu.role !== 'admin') {
            setUserToken(ut);
            setUser(uu);
            setAnalyticsUserId(uu._id);
        }
        const at = localStorage.getItem(ADMIN_TOKEN_KEY);
        const au = readJson<AuthUser>(ADMIN_KEY);
        if (at && au && au.role === 'admin') {
            setAdminToken(at);
            setAdmin(au);
        }
        setLoading(false);
    }, []);

    const loginUser = useCallback((userData: AuthUser, token: string) => {
        if (userData.role === 'admin') {
            throw new Error('Compte administrateur : utilisez le portail admin.');
        }
        localStorage.setItem(USER_TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setUserToken(token);
        setUser(userData);
        setAnalyticsUserId(userData._id);
    }, []);

    const logoutUser = useCallback(() => {
        localStorage.removeItem(USER_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUserToken(null);
        setUser(null);
        setAnalyticsUserId(null);
    }, []);

    const loginAdmin = useCallback((userData: AuthUser, token: string) => {
        if (userData.role !== 'admin') {
            throw new Error('Ce compte n’est pas administrateur.');
        }
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        localStorage.setItem(ADMIN_KEY, JSON.stringify(userData));
        setAdminToken(token);
        setAdmin(userData);
    }, []);

    const logoutAdmin = useCallback(() => {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_KEY);
        setAdminToken(null);
        setAdmin(null);
    }, []);

    const userValue: UserAuthContextType = {
        user,
        token: userToken,
        isAuthenticated: !!userToken && !!user,
        isAdmin: false,
        login: loginUser,
        logout: logoutUser,
        loading,
    };

    const adminValue: AdminAuthContextType = {
        user: admin,
        token: adminToken,
        isAuthenticated: !!adminToken && !!admin,
        login: loginAdmin,
        logout: logoutAdmin,
        loading,
    };

    return (
        <UserAuthContext.Provider value={userValue}>
            <AdminAuthContext.Provider value={adminValue}>
                {!loading && children}
            </AdminAuthContext.Provider>
        </UserAuthContext.Provider>
    );
};

/** Session utilisateur (builder, paiement, galerie) — indépendante de l’admin */
export const useAuth = () => {
    const context = useContext(UserAuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/** Session backoffice — indépendante de l’utilisateur */
export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (context === undefined) {
        throw new Error('useAdminAuth must be used within an AuthProvider');
    }
    return context;
};

export const getUserToken = () => localStorage.getItem(USER_TOKEN_KEY);
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);

export const isAdminAppPath = (pathname = window.location.pathname) =>
    pathname.startsWith('/bo-flashcv') || pathname.startsWith('/bo-fastcv') || pathname.startsWith('/admin');
